"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface NominatimResult {
  place_id: number;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
}

function formatAddress(result: NominatimResult): string {
  const a = result.address || {};
  const parts: string[] = [];
  if (a.house_number && a.road) parts.push(`${a.house_number} ${a.road}`);
  else if (a.road) parts.push(a.road);
  const city = a.city || a.town || a.village;
  if (city) parts.push(city);
  if (a.state) parts.push(a.state);
  if (a.postcode) parts.push(a.postcode);
  if (a.country_code === "ca" || a.country === "Canada") parts.push("Canada");
  return parts.join(", ") || result.display_name || "";
}

export interface SelectedAddress {
  line1: string;
  city: string;
  state: string;
  stateCode: string;
  zip: string;
  country: string;
}

interface AddressAutocompleteProps {
  value?: string;
  onChange: (value: string) => void;
  /** Called with structured address parts when the user picks a suggestion */
  onAddressSelect?: (addr: SelectedAddress) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  id?: string;
}

export function AddressAutocomplete({
  value = "",
  onChange,
  onAddressSelect,
  placeholder = "Start typing an address…",
  className,
  inputClassName,
  disabled,
  id,
}: AddressAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep input in sync if parent changes value externally
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/address/search?q=${encodeURIComponent(query)}`);
      if (!res.ok) {
        setSuggestions([]);
        setOpen(true);
        return;
      }
      const data = await res.json();
      const results: NominatimResult[] = Array.isArray(data.results) ? data.results : [];
      setSuggestions(results);
      setOpen(true);
    } catch {
      setSuggestions([]);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 400);
  };

  const handleSelect = (result: NominatimResult) => {
    const formatted = formatAddress(result);
    setInputValue(formatted);
    onChange(formatted);
    setSuggestions([]);
    setOpen(false);

    if (onAddressSelect) {
      const a = result.address;
      const line1Parts: string[] = [];
      if (a.house_number) line1Parts.push(a.house_number);
      if (a.road) line1Parts.push(a.road);
      onAddressSelect({
        line1: line1Parts.join(" "),
        city: a.city || a.town || a.village || "",
        state: a.state || "",
        stateCode: a.state || "",
        zip: a.postcode || "",
        country: a.country || "",
      });
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={["pl-9 pr-8", inputClassName].filter(Boolean).join(" ")}
          autoComplete="off"
          onFocus={() => suggestions.length > 0 && setOpen(true)}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {open && (
        <ul className="absolute z-[80] mt-1 w-full rounded-md border border-gray-200 bg-white text-gray-900 shadow-lg max-h-60 overflow-auto text-sm">
          {suggestions.length > 0 ? (
            suggestions.map((result) => {
              const label = formatAddress(result);
              return (
                <li
                  key={result.place_id}
                  className="flex items-start gap-2 px-3 py-2.5 cursor-pointer text-gray-900 hover:bg-blue-50 transition-colors"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(result);
                  }}
                >
                  <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-gray-400" />
                  <span className="leading-snug">{label}</span>
                </li>
              );
            })
          ) : (
            <li className="px-3 py-2.5 text-sm text-gray-500">
              {loading ? "Searching addresses…" : "No matching addresses. You can type yours and fill city, state, and ZIP or postal code below."}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
